import _ from "lodash";

import {POSTer} from "./helpers";
import {EventDispatcher} from "../eventing";
import {PROJECT_NAME, SEARCH} from "../config";
import store from "../store";

function filtersToSearchParams(filters, keymap = {}) {
  const queryString = new URLSearchParams();
  Object.keys(filters)
      .forEach((key) => {
        const filter = filters[key];

        // facetType is used for type-esque filters (ex: TIPE, book)
        // fieldName is used for instance-of-type filters (ex: Person, nco_0001)
        const rawFacetKey = filter.facetType ? `facet_${filter.facetType.toUpperCase()}` : `facet_${filter.fieldName.toUpperCase()}`;

        // the actual facet key used in the request can be mapped to add more flexibility
        const facetKey = keymap[rawFacetKey] || rawFacetKey;

        // filterType = (O, A), example facetValues: "Obook", "Operson", "Anco_0001"
        const facetValue = `${filter.filterType}${filter.fieldValue}`;

        queryString.append(facetKey, facetValue)
      })
  return queryString;
}

const filtersToString = (filters) => {
  return filtersToSearchParams(filters).toString();
};

function generateNonce() {
  return Math.random()
    .toString(36)
    .substring(2, 15);
}

function buildRequestPayload(props) {
  const requestPayload = {
    credentials: "include",
    headers: new Headers(),
    ...props,
  };

  requestPayload["headers"].append("X-PROJECTNAME", PROJECT_NAME);

  const csrfToken = store.getItem("SearcherCSRFToken");
  if (csrfToken) {
    requestPayload["headers"].append("X-CSRFToken", csrfToken);
  }
  return requestPayload;
}

export const getTranslation = (text, source, target, consumeResults) => {
  if (!text) return;
  let search_url = `${SEARCH.PATH_BASE}${SEARCH.PATH_TRANSLATE}`;

  const requestPayload = buildRequestPayload({
    body: JSON.stringify({ text, source, target }),
    cache: "no-cache",
    method: "POST",
  });

  fetch(search_url, requestPayload)
    .then((response) => response.json())
    .then((result) => {
      consumeResults(result);
    })
    .catch((e) => {
      console.log(e);
    });
};

class Searcher {
  constructor() {
    // Get the saved values from store
    this.dispatcher = new EventDispatcher();
    const savedValues = JSON.parse(store.getItem("SearcherSettings")) || {};
    this.searchTerm =
      (savedValues.searchTerm && savedValues.searchTerm.toString()) || "";
    this.cache = {};
    this.filters = savedValues.filters || {};
    this.ICClipboard = savedValues.ICClipboard || {};
    this.IClanguagecode = savedValues.IClanguagecode || "en";
    this.savedObjects = savedValues.savedObjects || {};
    this.credentials = savedValues.credentials || undefined;
    this.showAnnotations =
      savedValues.showAnnotations === true ||
      typeof savedValues === "undefined";
    this.updateFilterNonce();
    this.checkCredentialsExpiry();
  }

  saveSettings = () => {
    const settings = {
      searchTerm: this.searchTerm,
      filters: this.filters,
      ICClipboard: this.ICClipboard,
      IClanguagecode: this.IClanguagecode,
      savedObjects: this.savedObjects,
      credentials: this.credentials,
      showAnnotations: this.showAnnotations,
    };
    store.setItem("SearcherSettings", JSON.stringify(settings));

    if (this.csrfToken) {
      store.setItem("SearcherCSRFToken", this.csrfToken);
    }
  };

  addSavedItem = (obj) => {
    if (this.savedObjects[obj.ID[0]]) return;
    this.savedObjects[obj.ID[0]] = { timestamp: new Date(), obj: obj };
    this.saveSettings();
  };
  removeSavedItem = (objID) => {
    if (this.savedObjects[objID]) delete this.savedObjects[objID];
    this.saveSettings();
  };

  removeCredentials = () => {
    this.credentials = null;
  };

  addCredentials = (credentials) => {
    this.credentials = credentials;
    const url = `${SEARCH.PATH_BASE}${SEARCH.PATH_USER}`;

    POSTer(url, { token: credentials.token }, (data) => {
      this.credentials.userpk = data.userpk;
      this.csrfToken = data.csrfToken;
      this.saveSettings();
    });
  };

  checkCredentialsExpiry = () => {
    // Check to see if the current credentials has expired, if so, try to get a new one.
    if (this.credentials) {
      const expiry = new Date(parseFloat(this.credentials.exp) * 1000);
      if (expiry < new Date()) {
        //wipe credentials, for they are invalid
        this.credentials = null;
        this.saveSettings();

        document.location = `${SEARCH.AUTH_TOKEN_URL}${window.location.origin}${
          process.env.REACT_APP_URL_PATH
        }/`;
      }
    }
  };

  setICLanguage = (languagecode) => {
    this.IClanguagecode = languagecode;
    this.saveSettings();
  };

  hasFilterEntry = (fieldName, fieldValue) => {
    for (const id in this.filters) {
      const filter = this.filters[id];
      // check for filters with same field name and field value (TIPE, book) == (TIPE, book)
      const facetValueMatched = filter.fieldName === fieldName && filter.fieldValue === fieldValue;
      if (facetValueMatched) {
        return true;
      }

      // check for filters that share the same value (book, book)
      // but ensure that its not a simple facet type filter (fieldName != facetType)
      const valueFiltered = filter.fieldName !== filter.facetType && filter.fieldValue === fieldValue;
      if (valueFiltered) {
        return true;
      }
    }
    return false;
  };

  hasSearchFilters = () => {
    if (this.hasFacetFilters()) {
      return true;
    }
    return this.searchTerm.length > 0;
  };

  hasFacetFilters = () => {
    return Object.keys(this.filters).length > 0
  }

  updateFilterNonce = () => {
    this.nonce = generateNonce();
  };

  setSort = (section, sort) => {
    let r = this.getSectionResults(section);
    r.sort = sort;
    this.cache[section] = r;
  };

  getSectionResults = (section) => {
    if (!this.cache[section]) {
      // When total === null a search has not been performed yet
      this.cache[section] = {
        total: null,
        hits: [],
        facets: null,
        page: 0,
        sort: "ID",
        error: null,
        nonce: this.nonce,
      };
    }
    return this.cache[section];
  };

  // Return a citable URL
  citable_url = () => {
    const params = filtersToSearchParams(this.filters, {
      "facet_ICPATH": "facet_ic",
      "facet_TIPE": "facet_tipe"
    });

    if (this.searchTerm) {
      params.set('q', this.searchTerm);
    }

    return `${window.location.origin}${window.location.pathname}?${params.toString()}`;
  };

  // Called with a list of filters to be added.
  // Each entry in the list is an object with filterId, filterType, fieldName, fieldValue
  // If you pass in a filterId with filterType: null , you can delete that filter
  // Passing in an item with an existing filterType, fieldName, fieldValue deletes it.
  addFilter = (filterList) => {
    let thefilters = { ...this.filters };
    this.updateFilterNonce();
    for (var i = 0; i < filterList.length; i++) {
      let { filterId, filterType, fieldName, fieldValue, facetType } = filterList[i];

      // if the fieldName is ICPATH, record this notation
      if (fieldName === "ICPATH") {
        this.ICClipboard[fieldValue] = {
          timestamp: new Date(),
          notation: fieldValue,
        };
      }

      // Run through the existing filters, if there is already a fileter with the same type
      // and same fieldname, and fieldvalue delete it
      // this makes it a toggle
      for (let fkey in thefilters) {
        if (
          (thefilters[fkey].filterType === filterType) &
          (thefilters[fkey].fieldName === fieldName) &
          (thefilters[fkey].fieldValue === fieldValue)
        ) {
          filterId = thefilters[fkey].filterId;
          filterType = null; // deleting it
        }
      }

      // Specifying a filterType of null will delete this filter
      if (filterType === null) {
        delete thefilters[filterId];
        continue;
      }

      if (filterId === null) {
        filterId = generateNonce();
      }
      thefilters[filterId] = { filterId, filterType, fieldName, fieldValue, facetType };
    }
    this.filters = thefilters; // assign it new so updates in props for components become visible
    this.dispatcher.dispatch("filter.update", this.filters);
  };

  removeFilter = (filterId) => {
    if (this.filters[filterId]) {
      delete this.filters[filterId];
    }
  };

  removeFilterPredicate = (predicate) => {
    const predicateKeys = Object.keys(predicate);

    const removables = Object.keys(this.filters)
        .filter(id => {
          // returns true if all fields match the given predicate fields
          return predicateKeys.filter(pk => this.filters[id][pk] === predicate[pk]).length === predicateKeys.length
        })

    for (const id of removables) {
      delete this.filters[id];
    }

    // this.filters = filter_;
    this.dispatcher.dispatch("filter.update", this.filters);
  }

  searchObjectTypes = (q, type, context) => {
    const { page, size } = context.pagination || { page: 0, size: 1500 };
    const endpoint = SEARCH.PATH_BASE + SEARCH.PATH_SEARCH;
    const params = new URLSearchParams({
      facet_TIPE: `O${type}`,
      section: context.section,
      q,
      page,
      size,
      sort: context.sort || "RANDOM",
    });

    if (this.credentials && this.credentials.token) {
      params.append("token", this.credentials.token);
    }

    const requestPayload = buildRequestPayload();

    return fetch(endpoint + "?" + params.toString(), requestPayload)
      .then((response) => response.json())
      .catch((err) => console.error(err));
  };

  // Fetch a list of objects from the server as specified by the array "uids"
  getObjects = (uids, updateSearchResults) => {
    if (uids.length < 1) return;
    let search_url = `${SEARCH.PATH_BASE}${SEARCH.PATH_OBJECTS}`;

    const requestPayload = buildRequestPayload({
      body: JSON.stringify({ uids: uids }),
      cache: "no-cache",
      method: "POST",
    });

    fetch(search_url, requestPayload)
      .then((response) => response.json())
      .then((result) => {
        updateSearchResults(result);
      })
      .catch((e) => {
        updateSearchResults({ notfound: uids });
        console.log(e);
      });
  };

  // do a search action for a specific section
  doSearch = (
    section,
    updateSearchResults,
    searchTerm,
    filters,
    sortfield,
    size
  ) => {
    window._$tarQ &&
      window._$tarQ.push([
        "track",
        {
          datatype: "DATABASE",
          title: process.env.REACT_APP_PROJECT_NAME,
          itemtype: "SEARCH_REG",
        },
      ]);

    this.saveSettings();
    // for "normal" searches, the searchTerm and filters params are left out and stay undefined
    // when you want to do "custom" searches that are not fixed to a section,
    // it is called with section=null and filled in searchTerm, filters.
    // See views.js Children component
    let usedSearchTerm = searchTerm;
    if (searchTerm === undefined) {
      usedSearchTerm = this.searchTerm;
    }
    let sectionResults = this.getSectionResults(section);
    if (sortfield !== undefined) {
      sectionResults.sort = sortfield;
    }
    const hpp = size || SEARCH.DEFAULT_HPP;
    const CRED = this.credentials ? `token=${this.credentials.token}` : "";
    const search_url = `${SEARCH.PATH_BASE}${SEARCH.PATH_SEARCH}?${CRED}&${
      SEARCH.PARAM_SECTION
    }${section}&${SEARCH.PARAM_SEARCH}${usedSearchTerm}&${SEARCH.PARAM_PAGE}${
      sectionResults.page
    }&${SEARCH.PARAM_HPP}${hpp}&${SEARCH.PARAM_SORT}${
      sectionResults.sort
    }&${filtersToString(filters || this.filters)}`;

    // console.log(search_url);

    const requestPayload = {
      credentials: "include",
      headers: new Headers(),
    };

    requestPayload["headers"].append("X-PROJECTNAME", PROJECT_NAME);

    const csrfToken = store.getItem("SearcherCSRFToken");
    if (csrfToken) {
      requestPayload["headers"].append("X-CSRFToken", csrfToken);
    }

    return fetch(search_url, requestPayload)
      .then((response) => response.json())
      .then((result) => {
        if (result.error) {
          document.location = `${process.env.REACT_APP_URL_PATH}/help/trial`;
          return;
        }

        sectionResults.hits = this.addNextPrevious(result.hits);
        sectionResults.facets = result.facets;
        sectionResults.total = result.total;
        sectionResults.nonce = this.nonce;
        this.cache[section] = sectionResults;

        if (updateSearchResults) {
          updateSearchResults(sectionResults);
        }

        return result;
      })
      .catch((e) => {
        this.cache[section].error = e;
        this.cache[section].total = null;
        updateSearchResults(this.cache[section]);
      });
  };

  // add next/previous IDs to search results.
  addNextPrevious = (hits) => {
    let currentPrevious = null;
    hits.forEach((hit, index) => {
      if (hit.ID) {
        const ID = hit.ID[0];
        if (currentPrevious) {
          hit._searchPrevious = [currentPrevious];
          hits[index - 1]._searchNext = [ID];
        }
        currentPrevious = ID;
      }
    });
    return hits;
  };

  extractICfromHits = (section) => {
    let sectionResults = this.getSectionResults(section);
    let ICCounter = {};
    let firstDigit = {};
    sectionResults.hits.forEach(
      (obj) =>
        obj.IC &&
        obj.IC.forEach((anIC) => {
          if (anIC) {
            let current = (ICCounter[anIC] && ICCounter[anIC].current) || 0;
            ICCounter[anIC] = { current: current + 1, notation: anIC };
            firstDigit[anIC.charAt(0)] = [];
          }
        })
    );
    _.reverse(_.sortBy(ICCounter, [(o) => o.current])).forEach(
      (obj) =>
        obj.notation && firstDigit[obj.notation.charAt(0)].push(obj.notation)
    );
    return firstDigit;
  };

  subscribe(...args) {
    this.dispatcher.subscribe(...args);
  }

  unsubscribe(...args) {
    this.dispatcher.unsubscribe(...args);
  }

  toggleAnnotations = () => {
    this.showAnnotations = !this.showAnnotations;
    this.dispatcher.dispatch("update.showAnnotations", this.showAnnotations);
    this.saveSettings();
  };
}

export default Searcher;
